export type JobKind = 'personal' | 'copilot';
export interface AnalysisJob<T> {
  request_id: string; kind: JobKind;
  state: 'queued' | 'running' | 'complete' | 'failed' | 'interrupted' | 'unavailable';
  result?: T; error?: string;
}
const LABELS: Record<string, string> = {
  original_attempt_interrupted_no_repayment: '이전 분석이 중단됐습니다. 원래 호출 기록을 확인해 주세요. 같은 요청은 다시 과금하지 않습니다.',
  job_receipt_unreadable_no_repayment: '저장된 작업 기록을 읽지 못했습니다. 원래 호출 확인이 필요합니다.',
  job_receipt_completion_unavailable_no_repayment: '분석 완료 기록 저장에 실패했습니다. 원래 호출 영수증을 확인해 주세요. 자동 재과금하지 않습니다.',
  insufficient_evidence: '분석할 저장 근거가 부족합니다. 수집 상태를 확인해 주세요.',
  trace_persistence_failed: '응답 영수증이 DB 저장을 기다리고 있습니다. 원래 요청을 유지해 확인해 주세요.',
  analysis_job_failed_no_automatic_retry: '분석 작업이 완료되지 않았습니다. 관리의 원래 호출 기록을 확인해 주세요.',
};
export const pendingJobKey = (kind: JobKind) => `koru.pending-analysis-job.v1.${kind}`;
export function readPendingJob(kind: JobKind): Record<string, unknown> | null {
  try { return JSON.parse(localStorage.getItem(pendingJobKey(kind)) || 'null'); } catch { return null; }
}
export function savePendingJob(kind: JobKind, payload: Record<string, unknown> | null) {
  try {
    payload ? localStorage.setItem(pendingJobKey(kind), JSON.stringify(payload)) : localStorage.removeItem(pendingJobKey(kind));
  } catch { /* Network retries still retain the same ID in component memory. */ }
}
async function requestJob<T>(kind: JobKind, payload: Record<string, unknown>, resume: boolean, signal?: AbortSignal): Promise<AnalysisJob<T>> {
  const path = `/api/analysis-jobs/${kind}${resume ? '/' + encodeURIComponent(String(payload.request_id)) : ''}`;
  const response = await fetch(path, resume ? { signal } : {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal,
  });
  const value = await response.json();
  if (!response.ok) {
    const key = typeof value.detail === 'string' ? value.detail : value.error;
    const message = key === 'request_id_conflict' ? '같은 요청 ID의 내용이 다릅니다.' : key === 'analysis_job_not_found' ? '서버에 저장된 작업이 없습니다. 같은 요청으로 재시도할 수 있습니다.' : `작업 조회 실패 (${response.status}). 같은 요청으로 저장 상태를 확인할 수 있습니다.`;
    throw new Error(message);
  }
  return value;
}
export async function runAnalysisJob<T>(kind: JobKind, payload: Record<string, unknown>, options: { resume?: boolean; signal?: AbortSignal } = {}): Promise<T> {
  let job = await requestJob<T>(kind, payload, Boolean(options.resume), options.signal);
  while (job.state === 'queued' || job.state === 'running') {
    await new Promise<void>((resolve, reject) => {
      const signal = options.signal;
      if (signal?.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
      const cleanup = () => signal?.removeEventListener('abort', cancel);
      const timer = setTimeout(() => { cleanup(); resolve(); }, 2500);
      const cancel = () => { clearTimeout(timer); cleanup(); reject(new DOMException('Aborted', 'AbortError')); };
      signal?.addEventListener('abort', cancel, { once: true });
    });
    job = await requestJob<T>(kind, payload, true, options.signal);
  }
  if (job.state !== 'complete' || !job.result) {
    // Keep pending ID on a failure: a repeated submit returns the same terminal
    // evidence; only an explicit new analysis allocates another request ID.
    throw new Error(LABELS[job.error || ''] || '분석이 완료되지 않았습니다. 원래 작업 기록을 확인해 주세요.');
  }
  savePendingJob(kind, null);
  return job.result;
}
